import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormLabel,
  SxProps,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useId, useState } from "react";

const defaultSx: SxProps = {};

const ALL_SENTINEL: unique symbol = Symbol("multiSelectAll");
type SelectAllOption = typeof ALL_SENTINEL;
type Item<T> = T | SelectAllOption;

const isAll = <T,>(item: Item<T>): item is SelectAllOption =>
  item === ALL_SENTINEL;

interface Props<T, Id extends number | string> {
  sx?: SxProps;
  values: T[];
  options: T[];
  label: string;
  getOptionLabel: (option: T) => string;
  getOptionId: (option: T) => Id;
  disabled?: boolean;
  setValues: (newValues: T[]) => void;
  small?: boolean;
  limitTags?: number;
  selectAll?: boolean;
  labelAbove?: boolean;
}

export const MultiSelect = <T, Id extends number | string>({
  options,
  values,
  label,
  setValues,
  getOptionLabel,
  getOptionId,
  disabled = false,
  small,
  limitTags,
  selectAll,
  labelAbove,
  sx = defaultSx,
}: Props<T, Id>) => {
  const [inputValues, setInputValues] = useState("");
  const inputId = useId();
  const { t } = useTenantTranslation();
  const selectAllLabel = t("selectAll");

  const selectAllEnabled = selectAll ?? false;
  const allSelected =
    selectAllEnabled && options.length > 0 && values.length === options.length;

  const itemOptions: Item<T>[] = selectAllEnabled
    ? [ALL_SENTINEL, ...options]
    : options;

  const filterRealOptions = createFilterOptions<Item<T>>();

  const handleChange = (newValues: readonly Item<T>[]) => {
    if (newValues.some(isAll)) {
      setValues(allSelected ? [] : options);
    } else {
      setValues(newValues.filter((v): v is T => !isAll(v)));
    }
  };

  const autocomplete = (
    <Autocomplete<Item<T>, true>
      id={labelAbove ? inputId : undefined}
      sx={sx}
      disabled={disabled}
      options={itemOptions}
      value={values}
      onChange={(_, next) => {
        handleChange(next);
      }}
      inputValue={inputValues}
      onInputChange={(_, next) => {
        setInputValues(next);
      }}
      multiple
      disableCloseOnSelect
      limitTags={limitTags}
      filterOptions={
        selectAllEnabled
          ? (opts, state) => [
              ALL_SENTINEL,
              ...filterRealOptions(
                opts.filter((o) => !isAll(o)),
                state
              ),
            ]
          : undefined
      }
      isOptionEqualToValue={(a, b) =>
        isAll(a) || isAll(b) ? false : getOptionId(a) === getOptionId(b)
      }
      getOptionLabel={(option) =>
        isAll(option) ? selectAllLabel : getOptionLabel(option)
      }
      renderOption={({ key, ...props }, option) => {
        if (isAll(option)) {
          return (
            <Box
              component="li"
              key={key}
              {...props}
              aria-selected={allSelected}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {selectAllLabel}
            </Box>
          );
        }
        return (
          <li key={key} {...props}>
            {getOptionLabel(option)}
          </li>
        );
      }}
      renderValue={
        selectAllEnabled
          ? (value, getTagProps) => {
              if (allSelected) {
                const { key, ...tagProps } = getTagProps({ index: 0 });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    label={selectAllLabel}
                    size={small ? "small" : undefined}
                    onDelete={() => {
                      setValues([]);
                    }}
                  />
                );
              }
              const visibleCount = limitTags ?? 1;
              const shownValues = value.slice(0, visibleCount);
              const hiddenCount = value.length - shownValues.length;
              return (
                <>
                  {shownValues.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={
                          isAll(option)
                            ? selectAllLabel
                            : getOptionLabel(option)
                        }
                        size={small ? "small" : undefined}
                      />
                    );
                  })}
                  {hiddenCount > 0 ? (
                    <Chip
                      label={`+${hiddenCount}`}
                      size={small ? "small" : undefined}
                    />
                  ) : null}
                </>
              );
            }
          : undefined
      }
      renderInput={(params) =>
        labelAbove ? (
          <TextField {...params} />
        ) : (
          <TextField {...params} label={label} />
        )
      }
      size={small ? "small" : undefined}
    />
  );

  if (!labelAbove) {
    return autocomplete;
  }

  return (
    <FormControl
      size="small"
      sx={{
        gap: 0.5,
        alignSelf: "flex-end",
        "& .MuiInputBase-root": { height: 40, alignItems: "center" },
      }}
    >
      <FormLabel htmlFor={inputId}>{label}</FormLabel>
      {autocomplete}
    </FormControl>
  );
};
